import { createTheme } from '@mui/material/styles';

// Define custom colors for the Carbon Intelligence theme
const colors = {
  primary: {
    50: '#e8f5e8',
    100: '#c8e6c9',
    200: '#a5d6a7',
    300: '#81c784',
    400: '#66bb6a',
    500: '#4caf50', // Main green
    600: '#43a047',
    700: '#388e3c',
    800: '#2e7d32',
    900: '#1b5e20',
  },
  secondary: {
    50: '#e3f2fd',
    100: '#bbdefb',
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#2196f3', // Main blue
    600: '#1e88e5',
    700: '#1976d2',
    800: '#1565c0',
    900: '#0d47a1',
  },
  success: {
    50: '#e8f5e8',
    100: '#c8e6c9',
    200: '#a5d6a7',
    300: '#81c784',
    400: '#66bb6a',
    500: '#4caf50',
    600: '#43a047',
    700: '#388e3c',
    800: '#2e7d32',
    900: '#1b5e20',
  },
  warning: {
    50: '#fff8e1',
    100: '#ffecb3',
    200: '#ffe082',
    300: '#ffd54f',
    400: '#ffca28',
    500: '#ffc107',
    600: '#ffb300',
    700: '#ffa000',
    800: '#ff8f00',
    900: '#ff6f00',
  },
  error: {
    50: '#ffebee',
    100: '#ffcdd2',
    200: '#ef9a9a',
    300: '#e57373',
    400: '#ef5350',
    500: '#f44336',
    600: '#e53935',
    700: '#d32f2f',
    800: '#c62828',
    900: '#b71c1c',
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

/** Post-login workspace sidebar — deep forest green in every app theme mode. */
export const SIDEBAR_WORKSPACE_BACKGROUND = '#0a110e';
export const SIDEBAR_WORKSPACE_BORDER = 'rgba(76, 175, 80, 0.18)';
export const SIDEBAR_WORKSPACE_TEXT_PRIMARY = '#f1f5f9';
export const SIDEBAR_WORKSPACE_TEXT_SECONDARY = '#94a3b8';

export const createAppTheme = (mode: 'light' | 'dark') =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary[500],
        light: colors.primary[300],
        dark: colors.primary[700],
        contrastText: '#ffffff',
      },
      secondary: {
        main: colors.secondary[500],
        light: colors.secondary[300],
        dark: colors.secondary[700],
        contrastText: '#ffffff',
      },
      success: {
        main: colors.success[500],
        light: colors.success[300],
        dark: colors.success[700],
        contrastText: '#ffffff',
      },
      warning: {
        main: colors.warning[500],
        light: colors.warning[300],
        dark: colors.warning[700],
        contrastText: '#000000',
      },
      error: {
        main: colors.error[500],
        light: colors.error[300],
        dark: colors.error[700],
        contrastText: '#ffffff',
      },
      background:
        mode === 'light'
          ? {
              default: '#f4f6f9',
              paper: '#ffffff',
            }
          : {
              default: '#0a110e',
              paper: '#141f19',
            },
      text:
        mode === 'light'
          ? {
              primary: '#0f172a',
              secondary: '#475569',
            }
          : {
              primary: '#f1f5f9',
              secondary: '#94a3b8',
            },
    },
    typography: {
      fontFamily: '"DM Sans", "Inter", system-ui, -apple-system, "Segoe UI", sans-serif',
      h1: {
        fontSize: '2rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.025em',
      },
      h2: {
        fontSize: '1.625rem',
        fontWeight: 700,
        lineHeight: 1.25,
        letterSpacing: '-0.02em',
      },
      h3: {
        fontSize: '1.375rem',
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: '-0.015em',
      },
      h4: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.35,
        letterSpacing: '-0.01em',
      },
      h5: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '0.9375rem',
        fontWeight: 600,
        lineHeight: 1.45,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
      },
      button: {
        fontSize: '0.875rem',
        fontWeight: 600,
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: 12,
    },
    spacing: 8,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Align native form/control chrome with the active MUI palette mode.
          html: {
            colorScheme: mode,
          },
          body: {
            backgroundImage:
              mode === 'light'
                ? 'linear-gradient(180deg, #f8fafc 0%, #f4f6f9 100%)'
                : 'linear-gradient(180deg, #0a110e 0%, #0d1511 100%)',
            backgroundAttachment: 'fixed',
          },
          // Bare native <button> shells inherit themed text; MUI Button/ButtonBase
          // still set their own colors via higher-specificity class rules.
          button: {
            color: 'inherit',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '8px 16px',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
          },
        },
      },
      // DEEP-BUG-042: restore a visible keyboard focus ring (ButtonBase sets outline: none).
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': {
              outline: `2px solid ${mode === 'light' ? '#2e7d32' : '#ffffff'}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': {
              outline: `2px solid ${mode === 'light' ? '#2e7d32' : '#ffffff'}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: 12,
            border:
              mode === 'light'
                ? '1px solid rgba(148, 163, 184, 0.2)'
                : '1px solid rgba(148, 163, 184, 0.22)',
            boxShadow: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border:
              mode === 'light'
                ? '1px solid rgba(148, 163, 184, 0.16)'
                : '1px solid rgba(148, 163, 184, 0.2)',
          },
          elevation1: {
            boxShadow:
              mode === 'light'
                ? '0 1px 2px rgba(15, 23, 42, 0.06)'
                : '0 1px 3px rgba(0, 0, 0, 0.3)',
          },
          elevation2: {
            boxShadow:
              mode === 'light'
                ? '0 2px 4px rgba(15, 23, 42, 0.08)'
                : '0 2px 6px rgba(0, 0, 0, 0.35)',
          },
          elevation3: {
            boxShadow:
              mode === 'light'
                ? '0 4px 8px rgba(15, 23, 42, 0.1)'
                : '0 4px 10px rgba(0, 0, 0, 0.4)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
              '&.Mui-focused': {
                boxShadow:
                  mode === 'light'
                    ? '0 0 0 3px rgba(76, 175, 80, 0.12)'
                    : '0 0 0 3px rgba(76, 175, 80, 0.26)',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            backgroundImage: 'none',
          },
        },
      },
      MuiContainer: {
        defaultProps: {
          maxWidth: 'xl',
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            alignItems: 'flex-start',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            height: 6,
          },
        },
      },
      MuiStepper: {
        styleOverrides: {
          root: {
            '& .MuiStepLabel-label': {
              fontWeight: 500,
            },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            maxHeight: 'min(90dvh, 90vh)',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          },
        },
      },
    },
  });

const theme = createAppTheme('light');

export default theme;